import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { ShieldCheck, Save, RefreshCw, RotateCcw, HelpCircle } from "lucide-react";
import GuidedTour from "../../components/common/GuidedTour";

const PERM_COLOR = {
  none: "bg-white text-ink-muted border-ink/10",
  read: "bg-navy/5 text-navy border-navy/25",
  write: "bg-yellow-400/25 text-navy border-yellow-500/60 font-semibold",
  admin: "bg-navy text-yellow-300 border-navy font-semibold",
};

/**
 * Super-Admin editable matrix of role → module permissions.
 *
 * The API returns the *default* matrix (from code) plus any persisted
 * `overrides`. Cells with a stored override are highlighted so ops can see
 * at a glance which permissions deviate from the seeded defaults. Saving
 * writes the merged matrix back so overrides persist even if defaults later
 * change.
 */
export default function RolesCMS() {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});     // effective (edited) matrix
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const TOUR_KEY = "venturaz.roles_cms.tour_v1";

  // Auto-open the tour the first time an admin lands here.
  useEffect(() => {
    if (loading || !data) return;
    try {
      if (!localStorage.getItem(TOUR_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, [loading, data]);

  const TOUR_STEPS = useMemo(() => {
    if (!data) return [];
    const firstRole = data.roles?.[0] || "super_admin";
    const firstMod = data.modules?.[0] || "debt_leads";
    return [
      {
        selector: "admin-roles-cms",
        title: "The permission matrix",
        body: "Every row is a module in the app. Every column is a role. The cell in between decides what that role can do with that module — none, read, write or admin.",
      },
      {
        selector: `perm-${firstRole}-${firstMod}`,
        title: "One cell, one permission",
        body: "Click any cell to change its level. Cells inherit code defaults until you override them here. Save persists your overrides on top of the defaults, so future default changes still apply where you haven't overridden.",
      },
      {
        selector: "roles-override-hint",
        title: "Yellow dot = override",
        body: "Any cell with a yellow dot has been overridden from the code default. Click the dot to revert just that one cell, or use ‘Revert all to default’ to reset the whole matrix.",
      },
      {
        selector: "roles-revert-all",
        title: "Revert all",
        body: "Nuclear option — clears every override and puts you back on the code defaults. Useful after experimenting.",
      },
      {
        selector: "roles-save",
        title: "Save the matrix",
        body: "Nothing persists until you save. Server merges your overrides with the defaults and every debt route re-reads the effective matrix within 30 seconds.",
      },
    ];
  }, [data]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roles-cms");
      setData(data);
      // Effective = defaults overlaid with overrides.
      const eff = {};
      for (const role of data.roles) {
        eff[role] = { ...(data.defaults?.[role] || {}), ...(data.overrides?.[role] || {}) };
      }
      setMatrix(eff);
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const perms = data?.perm_levels || ["none", "read", "write", "admin"];

  const setCell = (role, mod, perm) => {
    setMatrix((m) => ({ ...m, [role]: { ...(m[role] || {}), [mod]: perm } }));
    setDirty(true);
  };

  const isOverride = (role, mod) => {
    if (!data) return false;
    const def = data.defaults?.[role]?.[mod];
    const cur = matrix?.[role]?.[mod];
    return def !== cur;
  };

  const revertCell = (role, mod) => {
    if (!data) return;
    const def = data.defaults?.[role]?.[mod] ?? "none";
    setCell(role, mod, def);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send the full effective matrix; server clamps invalid entries.
      await api.patch("/admin/roles-cms", { matrix });
      toast.success("Permission matrix saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const revertAll = () => {
    if (!data) return;
    setMatrix(JSON.parse(JSON.stringify(data.defaults)));
    setDirty(true);
  };

  const overrideCount = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    for (const role of data.roles) {
      for (const mod of data.modules) {
        if (data.defaults?.[role]?.[mod] !== matrix?.[role]?.[mod]) n += 1;
      }
    }
    return n;
  }, [data, matrix]);

  if (loading || !data) {
    return (
      <DashboardShell title="Roles CMS" subtitle="Loading…">
        <div className="text-sm italic text-ink-muted">Loading permission matrix…</div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Roles CMS" subtitle="Admin · Permission Matrix (module × role)">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-roles-cms">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-ink-muted" data-testid="roles-override-hint">
            <ShieldCheck size={13} className="text-navy" />
            <span>Cells with a background pill overlay a stored override on top of the code defaults.</span>
            {overrideCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-400/30 text-navy font-semibold">
                {overrideCount} override{overrideCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTourOpen(true)}
              className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
              data-testid="roles-tour-launch"
            >
              <HelpCircle size={11} /> Take a tour
            </button>
            <button
              type="button"
              onClick={load}
              className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
              data-testid="roles-refresh"
            >
              <RefreshCw size={11} /> Refresh
            </button>
            <button
              type="button"
              onClick={revertAll}
              className="text-[11px] font-heading text-red-600 hover:bg-red-50 rounded px-2 py-1 border border-red-200 inline-flex items-center gap-1"
              data-testid="roles-revert-all"
            >
              <RotateCcw size={11} /> Revert all to default
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="text-[11px] font-heading text-navy bg-yellow-400 hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed rounded px-3 py-1 inline-flex items-center gap-1"
              data-testid="roles-save"
            >
              <Save size={11} /> {saving ? "Saving…" : "Save matrix"}
            </button>
          </div>
        </div>

        <div className="overflow-auto border border-ink/10 rounded-lg bg-white">
          <table className="w-full text-[12px]">
            <thead className="bg-navy/5 text-navy sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 border-b border-ink/10 sticky left-0 bg-navy/5">Module</th>
                {data.roles.map((r) => (
                  <th
                    key={r}
                    className="text-left px-2 py-2 border-b border-ink/10 whitespace-nowrap font-heading"
                    title={r}
                  >
                    {data.role_labels?.[r] || r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.modules.map((mod, idx) => (
                <tr key={mod} className={idx % 2 ? "bg-ink/[0.02]" : ""}>
                  <td className="px-3 py-1.5 border-b border-ink/5 font-mono text-[11px] whitespace-nowrap sticky left-0 bg-inherit">
                    {mod}
                  </td>
                  {data.roles.map((role) => {
                    const val = matrix?.[role]?.[mod] || "none";
                    return (
                      <td key={role} className="px-1 py-1 border-b border-ink/5">
                        <div className="relative">
                          <select
                            value={val}
                            onChange={(e) => setCell(role, mod, e.target.value)}
                            data-testid={`perm-${role}-${mod}`}
                            className={`w-full text-[11px] px-1.5 py-1 rounded border ${PERM_COLOR[val] || PERM_COLOR.none} focus:outline-none focus:ring-1 focus:ring-navy`}
                          >
                            {perms.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                          {isOverride(role, mod) && (
                            <button
                              type="button"
                              onClick={() => revertCell(role, mod)}
                              title="Revert this cell to default"
                              className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-yellow-400 text-navy text-[8px] leading-3 hover:bg-yellow-500"
                            >
                              •
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-ink-muted italic">
          Legend: <span className="mx-1 px-1.5 rounded border border-ink/10 bg-white">none</span>
          <span className="mx-1 px-1.5 rounded border border-navy/25 bg-navy/5 text-navy">read</span>
          <span className="mx-1 px-1.5 rounded border border-yellow-500/60 bg-yellow-400/25 text-navy">write</span>
          <span className="mx-1 px-1.5 rounded border-navy bg-navy text-yellow-300">admin</span>
        </div>

        {data.updated_at && (
          <div className="text-[10px] text-ink-muted">
            Last saved <span className="font-mono">{new Date(data.updated_at).toLocaleString()}</span>
            {data.updated_by && <> by <span className="font-mono">{data.updated_by}</span></>}
          </div>
        )}
      </div>
      <GuidedTour
        steps={TOUR_STEPS}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        storageKey={TOUR_KEY}
      />
    </DashboardShell>
  );
}
